import React from 'react';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  SwapVert as SortIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  SORT_LABEL,
  SORT_OPTIONS,
  SortOption,
  TYPE_TABS,
  TypeFilter,
} from './constants';

interface FormListToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  type: TypeFilter;
  onTypeChange: (value: TypeFilter) => void;
  sort: SortOption;
  onSortChange: (value: SortOption) => void;
  onRefresh?: () => Promise<void> | void;
}

export const FormListToolbar: React.FC<FormListToolbarProps> = ({
  search,
  onSearchChange,
  type,
  onTypeChange,
  sort,
  onSortChange,
  onRefresh,
}) => {
  const [sortAnchor, setSortAnchor] = React.useState<null | HTMLElement>(null);
  const [refreshing, setRefreshing] = React.useState(false);

  const handleRefresh = async () => {
    if (!onRefresh || refreshing) return;
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <Box className="form-list-toolbar">
      <Box className="form-list-toolbar-primary">
        <TextField
          className="form-list-search"
          placeholder="Search forms"
          size="small"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon
                  sx={{ fontSize: 17, color: 'var(--admin-text-faint)' }}
                />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear search"
                  onClick={() => onSearchChange('')}
                >
                  <CloseIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />

        <Box className="form-list-toolbar-row">
          <ToggleButtonGroup
            className="admin-segmented form-list-tabs"
            value={type}
            exclusive
            onChange={(_e, value) => value && onTypeChange(value)}
            size="small"
          >
            {TYPE_TABS.map((tab) => (
              <ToggleButton key={tab.value} value={tab.value}>
                {tab.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Box className="form-list-toolbar-secondary">
            {onRefresh && (
              <IconButton
                className="form-list-refresh-btn"
                aria-label="Refresh forms"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <RefreshIcon
                  sx={{
                    fontSize: 18,
                    animation: refreshing ? 'form-list-refresh-spin 0.8s linear infinite' : 'none',
                  }}
                />
              </IconButton>
            )}

            <Button
              className="form-list-sort-btn"
              variant="outlined"
              startIcon={<SortIcon sx={{ fontSize: 16 }} />}
              onClick={(e) => setSortAnchor(e.currentTarget)}
            >
              <span className="form-list-sort-btn-label">{SORT_LABEL[sort]}</span>
            </Button>
          </Box>
        </Box>
      </Box>

      <Menu
        className="form-list-menu"
        anchorEl={sortAnchor}
        open={Boolean(sortAnchor)}
        onClose={() => setSortAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {SORT_OPTIONS.map((key) => (
          <MenuItem
            key={key}
            selected={sort === key}
            onClick={() => {
              onSortChange(key);
              setSortAnchor(null);
            }}
            sx={{ fontWeight: sort === key ? 700 : 400 }}
          >
            {SORT_LABEL[key]}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};
